import React, { useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';

import { myProfile } from 'Redux/user-profile/selectors';
import { useClassroomInterruption } from 'Util/Hooks';
import { useGetFeedback } from 'Util/FeedBackService';
import {
  FeedbackImprovementAndSatisfiedPlatform,
  useNPSState,
  NPS_STATE,
} from 'Containers/Feedback';
import useClassroomVideoPlayer from 'Util/Hooks/useClassroomVideoPlayer';
import { useConfig } from 'Context/ConfigContext';
import { VIDEO_STATE } from 'Context/constant';

const timeMillisecond = 24 * 60 * 60 * 1000;

const CourseFeedbackImprovementAndSatisfiedModal = ({ shouldShowReview, userId }) => {
  const { feedback } = useConfig();
  const dateDurationConfig = feedback?.npsPlatform?.date_duration ?? 180;
  const now = new Date();

  const {
    isInterrupting,
    activateInterruption,
    deactivateInterruption,
    courseData: { courseCode, progressPercent },
  } = useClassroomInterruption();

  const { data: reviewData, loading: feedbackLoading } = useGetFeedback({
    templateId: 'IMPROVEMENT_SATISFIED_FEEDBACK',
    contentId: courseCode,
    userId,
  });
  const { hasPlayer, videoState } = useClassroomVideoPlayer();

  const [currentNPSState, updateNPSState] = useNPSState({ courseCode });

  const isReviewed = useMemo(
    () =>
      !!reviewData && Array.isArray(reviewData.getFeedback) && reviewData.getFeedback.length !== 0,
    [reviewData]
  );

  useEffect(() => {
    const shouldShowAt60Percent =
      progressPercent >= 60 && currentNPSState === NPS_STATE.PROGRESS_60;
    const shouldShowAt80Percent =
      progressPercent >= 80 && currentNPSState === NPS_STATE.PROGRESS_80;
    const shouldShowAtTime =
      !isNaN(currentNPSState) &&
      now > new Date(currentNPSState + dateDurationConfig * timeMillisecond);
    const shouldShowOnSubsectionComplete =
      shouldShowReview && (videoState === VIDEO_STATE.ENDED || !hasPlayer);

    if (
      shouldShowOnSubsectionComplete &&
      !isReviewed &&
      !isNil(currentNPSState) &&
      (shouldShowAt60Percent || shouldShowAt80Percent || shouldShowAtTime)
    ) {
      window.postMessage('VIDEO_ENDING_OVERLAY_MODAL_INTERRUPT');
      if (!feedbackLoading) activateInterruption();
    }
  }, [feedbackLoading, currentNPSState, isReviewed, progressPercent, shouldShowReview, videoState]);

  const handleSubmitFeedback = () => {
    updateNPSState(NPS_STATE.NONE);
    window.postMessage('VIDEO_ENDING_OVERLAY_MODAL_CONTINUE');
    deactivateInterruption();
  };

  const handleCancelFeedback = () => {
    /**        ┌-------------------------------------↴
     * PROGRESS_60 --------> PROGRESS_80 --------> TIME --┐
     *         |                  ↓                  | └--┘
     *         └-------------->  NONE <------------- ┘
     */
    if (currentNPSState !== NPS_STATE.NONE) {
      if (NPS_STATE.PROGRESS_60 && progressPercent < 80) updateNPSState(NPS_STATE.PROGRESS_80);
      else updateNPSState(now.getTime());
    }
    window.postMessage('VIDEO_ENDING_OVERLAY_MODAL_CONTINUE');
    deactivateInterruption();
  };

  return (
    <FeedbackImprovementAndSatisfiedPlatform
      isShow={isInterrupting}
      handleSubmitFeedback={handleSubmitFeedback}
      handleCancelFeedback={handleCancelFeedback}
    />
  );
};

const CourseFeedbackModal = connect((state) => {
  const user = myProfile(state);
  return {
    userId: user.id,
  };
}, {})(CourseFeedbackImprovementAndSatisfiedModal);

export default CourseFeedbackModal;
